import { useCallback, useMemo, useReducer } from 'react';
import type { CabinConfiguration, SeatClass } from '../../../domain/models';
import { cabinsEqual, withSeatsOfClass } from './cabinFormat';

/**
 * Editable copy of a cabin configuration. Values are stored exactly as the
 * player enters them (no clamping), so the domain validator can report
 * out-of-range input instead of the UI silently hiding it.
 */

type DraftAction =
  | { readonly type: 'SET_SEATS'; readonly seatClass: SeatClass; readonly value: number }
  | { readonly type: 'SET_ECONOMY_PITCH'; readonly value: number }
  | { readonly type: 'SET_BUSINESS_PITCH'; readonly value: number }
  | { readonly type: 'SET_GALLEYS'; readonly value: number }
  | { readonly type: 'SET_WIFI'; readonly value: boolean }
  | { readonly type: 'SET_POWER'; readonly value: boolean }
  | { readonly type: 'REPLACE'; readonly cabin: CabinConfiguration };

function draftReducer(state: CabinConfiguration, action: DraftAction): CabinConfiguration {
  switch (action.type) {
    case 'SET_SEATS':
      return withSeatsOfClass(state, action.seatClass, action.value);
    case 'SET_ECONOMY_PITCH':
      return { ...state, economyPitchInches: action.value };
    case 'SET_BUSINESS_PITCH':
      return { ...state, businessPitchInches: action.value };
    case 'SET_GALLEYS':
      return { ...state, galleyCount: action.value };
    case 'SET_WIFI':
      return { ...state, wifiEquipped: action.value };
    case 'SET_POWER':
      return { ...state, inSeatPower: action.value };
    case 'REPLACE':
      return action.cabin;
  }
}

export interface CabinDraft {
  readonly cabin: CabinConfiguration;
  /** True when the draft differs from the baseline it was started from. */
  readonly isDirty: boolean;
  readonly setSeats: (seatClass: SeatClass, value: number) => void;
  readonly setEconomyPitch: (value: number) => void;
  readonly setBusinessPitch: (value: number) => void;
  readonly setGalleys: (value: number) => void;
  readonly setWifi: (value: boolean) => void;
  readonly setPower: (value: boolean) => void;
  /** Replaces the whole draft (presets, reset). */
  readonly replace: (cabin: CabinConfiguration) => void;
}

/**
 * @param baseline The configuration the draft starts from and is compared
 *                 against. The hook only reads it on first render, so remount
 *                 the owning component (via `key`) to start from a new one.
 */
export function useCabinDraft(baseline: CabinConfiguration): CabinDraft {
  const [cabin, dispatch] = useReducer(draftReducer, baseline);

  const setSeats = useCallback(
    (seatClass: SeatClass, value: number): void => dispatch({ type: 'SET_SEATS', seatClass, value }),
    []
  );
  const setEconomyPitch = useCallback(
    (value: number): void => dispatch({ type: 'SET_ECONOMY_PITCH', value }),
    []
  );
  const setBusinessPitch = useCallback(
    (value: number): void => dispatch({ type: 'SET_BUSINESS_PITCH', value }),
    []
  );
  const setGalleys = useCallback(
    (value: number): void => dispatch({ type: 'SET_GALLEYS', value }),
    []
  );
  const setWifi = useCallback((value: boolean): void => dispatch({ type: 'SET_WIFI', value }), []);
  const setPower = useCallback((value: boolean): void => dispatch({ type: 'SET_POWER', value }), []);
  const replace = useCallback(
    (next: CabinConfiguration): void => dispatch({ type: 'REPLACE', cabin: next }),
    []
  );

  const isDirty = useMemo(() => !cabinsEqual(cabin, baseline), [cabin, baseline]);

  return { cabin, isDirty, setSeats, setEconomyPitch, setBusinessPitch, setGalleys, setWifi, setPower, replace };
}
